import { BASE_WIDGET_EVENTS } from '@ptek/widget-core';
import type { RawTemplate } from '../types/templates';
import { ContextDataItem } from '../types/widget';

/**
 * Events that the widget sends to the host
 * (Host can listen to these)
 */
const EVENTS_WIDGET_SENDS = {
  ...BASE_WIDGET_EVENTS,

  EXPORT_DOCUMENT: 'exportDocument',
  DOCUMENT_SAVED: 'documentSaved',
  SAVE_SESSION_ID: 'saveSessionId',
  REQUEST_ENTER_FULLSCREEN: 'requestEnterFullscreen',
  REQUEST_EXIT_FULLSCREEN: 'requestExitFullscreen',
  REQUEST_TOGGLE_FULLSCREEN: 'requestToggleFullscreen',
  REQUEST_MINIMIZE: 'requestMinimize',
  REQUEST_RESTORE: 'requestRestore',
  REQUEST_TOGGLE_MINIMIZE: 'requestToggleMinimize',
  REQUEST_CLOSE: 'requestClose',
  CONFIRM_CLOSE: 'confirmClose',
  REQUEST_TEMPLATE_DROPDOWN_ACTION: 'requestTemplateDropdownAction',
  GENERATION_STARTED: 'generationStarted',
  GENERATION_ERROR: 'generationError',
} as const;

/**
 * Events that the widget receives from the host
 * (Host can emit these)
 */
const EVENTS_WIDGET_RECEIVES = {
  SET_ENCOUNTER: 'setEncounter',
  SET_CONTEXT_DATA: 'setContextData',
  SET_TEMPLATES: 'setTemplates',
} as const;

/**
 * Events from the host perspective
 * - LISTENER_NAMES: Events the host can listen to (widget sends these)
 * - EMITTER_NAMES: Events the host can emit (widget receives these)
 */
export const EVENTS = {
  LISTENER_NAMES: EVENTS_WIDGET_SENDS,
  EMITTER_NAMES: EVENTS_WIDGET_RECEIVES,
} as const;

/**
 * Events from the widget perspective
 * - EMITTER_NAMES: Events the widget can emit (host receives these)
 * - LISTENER_NAMES: Events the widget can listen to (host sends these)
 */
export const WIDGET_EVENTS = {
  EMITTER_NAMES: EVENTS_WIDGET_SENDS,
  LISTENER_NAMES: EVENTS_WIDGET_RECEIVES,
} as const;

/**
 * Type helper to get all event names
 */
export type AmbientScribeEventName =
  | (typeof EVENTS_WIDGET_SENDS)[keyof typeof EVENTS_WIDGET_SENDS]
  | (typeof EVENTS_WIDGET_RECEIVES)[keyof typeof EVENTS_WIDGET_RECEIVES];

/**
 * Event data types for each event
 */
export interface AmbientScribeEventData {
  [EVENTS_WIDGET_SENDS.MOUNTED]: { id: string };
  [EVENTS_WIDGET_SENDS.READY]: { id: string };
  [EVENTS_WIDGET_SENDS.SHOW]: { id: string };
  [EVENTS_WIDGET_SENDS.HIDE]: { id: string };
  [EVENTS_WIDGET_SENDS.UNMOUNTED]: { id: string };
  [EVENTS_WIDGET_SENDS.RESIZE]: { size: { width: number; height: number } };
  [EVENTS_WIDGET_SENDS.FULLSCREEN_ENTERED]: { id: string };
  [EVENTS_WIDGET_SENDS.FULLSCREEN_EXITED]: { id: string };
  [EVENTS_WIDGET_SENDS.CONTEXT_UPDATED]: { context: unknown };
  [EVENTS_WIDGET_SENDS.ERROR]: { message: string; error?: Error };

  [EVENTS_WIDGET_SENDS.EXPORT_DOCUMENT]: {
    document: {
      id: string;
      type: string;
      content: string;
      patientId?: string;
      patientName?: string;
      encounterId?: string;
      sessionId?: string;
    };
    exportAction: string;
    transcriptText?: string;
  };

  [EVENTS_WIDGET_SENDS.DOCUMENT_SAVED]: {
    documentId: string;
    title: string;
    content: string;
    patientId?: string;
    savedAt: string;
    wordCount: number;
  };

  [EVENTS_WIDGET_SENDS.SAVE_SESSION_ID]: {
    sessionId: string;
    encounterId?: string;
  };

  [EVENTS_WIDGET_SENDS.REQUEST_ENTER_FULLSCREEN]: Record<string, never>;
  [EVENTS_WIDGET_SENDS.REQUEST_EXIT_FULLSCREEN]: Record<string, never>;
  [EVENTS_WIDGET_SENDS.REQUEST_TOGGLE_FULLSCREEN]: Record<string, never>;
  [EVENTS_WIDGET_SENDS.REQUEST_MINIMIZE]: Record<string, never>;
  [EVENTS_WIDGET_SENDS.REQUEST_RESTORE]: Record<string, never>;
  [EVENTS_WIDGET_SENDS.REQUEST_TOGGLE_MINIMIZE]: Record<string, never>;
  [EVENTS_WIDGET_SENDS.CONFIRM_CLOSE]: Record<string, never>;
  [EVENTS_WIDGET_SENDS.REQUEST_TEMPLATE_DROPDOWN_ACTION]: { actionId: string };
  /**
   * Emitted when generation begins (no job_id). When the server enqueues
   * background generation (HTTP 202), a second generationStarted follows
   * carrying job_id — hosts should treat that one as the enqueue notification.
   */
  [EVENTS_WIDGET_SENDS.GENERATION_STARTED]: {
    sessionId: string;
    job_id?: string;
  };
  [EVENTS_WIDGET_SENDS.GENERATION_ERROR]: { sessionId: string };

  [EVENTS_WIDGET_RECEIVES.SET_ENCOUNTER]: {
    appointment: {
      id: string;
      patientName: string;
      title: string;
      time: string;
      serviceDate: string;
      chartNoteFont?: {
        fontFamily: string;
        fontSize: string;
      };
    };
  };

  [EVENTS_WIDGET_RECEIVES.SET_CONTEXT_DATA]: {
    contextData: ContextDataItem[];
  };

  [EVENTS_WIDGET_RECEIVES.SET_TEMPLATES]: {
    templates: RawTemplate[];
    autoSelectTemplateId?: string;
  };
}
